import { parse, stringify } from 'query-string';
import EventEmitter from './event-emitter';
import { Storage } from './storage';
import { Token } from './token';
import {
  ILocale,
  IAceClientOptions,
  ISignUpOptions,
  IPrivateTokens,
  IStorageData,
  ITokens,
  IFlags
} from './types';
import { logger } from './logger';

const ORIGIN_KEY = 'ace-sdk-origin';

class CreateClient extends EventEmitter {
  static throwCustomError(param: string): void {
    throw new Error(`Invalid param ${param}.`);
  }

  private storage: Storage;
  private aceEndpoint: IAceClientOptions['aceEndpoint'];
  private ownAceEndpoint: IAceClientOptions['ownAceEndpoint'];
  private clientId: IAceClientOptions['clientId'];
  private defaultRedirectUri: IAceClientOptions['defaultRedirectUri'];
  private signInRedirectUri: IAceClientOptions['signInRedirectUri'];
  private signUpRedirectUri: IAceClientOptions['signUpRedirectUri'];
  private signOutRedirectUri: IAceClientOptions['signOutRedirectUri'];
  private locale: IAceClientOptions['locale'];
  private flags: IFlags;
  private initialTokensFetchDone: boolean;
  private refreshTokenTimout?: NodeJS.Timeout;
  private autorefresh: boolean;

  constructor({
    clientId,
    defaultRedirectUri,
    signInRedirectUri,
    signUpRedirectUri,
    signOutRedirectUri,
    aceEndpoint,
    ownAceEndpoint,
    locale = 'en-US',
    onTokens,
    autorefresh = false,
    flags = {},
    ...options
  }: IAceClientOptions) {
    super();
    this.storage = new Storage(options);

    logger.setOptions({ verbose: Boolean(options.debug) });

    if (!clientId) {
      CreateClient.throwCustomError('clientId');
    }

    if (!aceEndpoint) {
      CreateClient.throwCustomError('aceEndpoint');
    }

    this.aceEndpoint = aceEndpoint;
    this.clientId = clientId;
    this.defaultRedirectUri = defaultRedirectUri;
    this.signInRedirectUri = signInRedirectUri;
    this.signUpRedirectUri = signUpRedirectUri;
    this.signOutRedirectUri = signOutRedirectUri;
    this.ownAceEndpoint = ownAceEndpoint;
    this.locale = locale;
    this.initialTokensFetchDone = false;
    this.autorefresh = autorefresh;
    this.flags = flags;

    if (onTokens) {
      this.on('tokens', onTokens);
    }
    logger.log('constructor initialized.');
  }

  // tslint:disable-next-line:cognitive-complexity
  async getTokens(options?: { force: boolean }): Promise<IPrivateTokens> {
    logger.log('getTokens()', options);
    const force = (options && options.force) || false;
    // first time and has ownAceEndpoint?
    const forceFetch = !this.initialTokensFetchDone || force;
    if (forceFetch && this.ownAceEndpoint) {
      // YES
      logger.log('First time call and ownAceEndpoint is defined. Try fetch');
      return this.fetchTokens()
        .catch(async e => {
          if (isUnauthorized(e.status)) {
            await this.storage.clear();
          }
          return Promise.reject(e);
        })
        .then(async (newTokens: IPrivateTokens) => {
          logger.log('fetch result:', newTokens);
          await this.maybeWriteTokens(newTokens);
          this.scheduleRefreshTokens(newTokens);
          return newTokens;
        });
    }
    // NO
    const tokensFromUrl = this.parseTokensFromUrl();
    if (tokensFromUrl && !Token.isExpired(tokensFromUrl.access_token)) {
      await this.scheduleRefreshTokens(tokensFromUrl);
      await this.maybeWriteTokens(tokensFromUrl);
      return tokensFromUrl;
    }
    const tokensFromStorage = await this.readTokensFromStorage();
    if (tokensFromStorage) {
      if (Token.isExpired(tokensFromStorage.access_token)) {
        logger.log('Access token expired');
        await this.storage.clear();
      } else {
        await this.scheduleRefreshTokens(tokensFromStorage);
        return tokensFromStorage;
      }
    }
    return this.ownAceEndpoint
      ? this.fetchTokens()
          .catch(async error => {
            if (isUnauthorized(error.status)) {
              await this.storage.clear();
            }
            return Promise.reject(error);
          })
          .then(async (tokens: IPrivateTokens) => {
            logger.log('fetch returned', tokens);
            await this.maybeWriteTokens(tokens);
            this.scheduleRefreshTokens(tokens);
            return tokens;
          })
      : Promise.reject({});
  }

  // tslint:disable-next-line:cognitive-complexity
  async refreshTokens(): Promise<IPrivateTokens> {
    logger.log('refreshTokens()');
    // first time and has ownAceEndpoint?
    if (this.ownAceEndpoint) {
      // YES
      const nosso = Boolean(this.flags.nosso);
      const params = nosso ? '?nosso=1' : '';
      return fetch(`${this.ownAceEndpoint}/refresh-tokens${params}`, { credentials: 'include' })
        .then(refreshTokensResponse => {
          logger.log('/refresh-tokens response', refreshTokensResponse);
          if (refreshTokensResponse.status === 200) {
            return refreshTokensResponse
              .json()
              .then(parsedTokens => {
                logger.log('tokens parsed');
                return parsedTokens;
              })
              .catch(() => {
                logger.log('tokens invalid json');
                Promise.reject('Invalid json received');
              });
          }

          return Promise.reject(refreshTokensResponse);
        })
        .catch(async e => {
          if (isUnauthorized(e.status)) {
            await this.storage.clear();
          }
          this.emit('tokens', null);
          return Promise.reject(e);
        })
        .then(async (refreshedTokens: IPrivateTokens) => {
          logger.log('fetch refresh tokens:', refreshedTokens);
          await this.maybeWriteTokens(refreshedTokens);
          this.scheduleRefreshTokens(refreshedTokens);
          return refreshedTokens;
        });
    }
    return Promise.reject('No ownAceEndpoint');
  }

  signIn(): Promise<IPrivateTokens> {
    logger.log('signIn()');

    return this.getTokens().catch(async e => {
      if ((this.ownAceEndpoint && isUnauthorized(e.status)) || !this.ownAceEndpoint) {
        logger.log('tokens not found. try to login');
        return this.setOriginUrl()
          .then(() => this.redirectToSignIn())
          .then(() => {
            logger.log('Not redirected. Rejecting');
            return Promise.reject(e);
          });
      }

      logger.log('tokens not found');
      return Promise.reject(e);
    });
  }

  signUp(params?: ISignUpOptions): Promise<void> {
    logger.log('signUp()', params);
    return this.setOriginUrl().then(() => {
      logger.log('redirecting');
      const redirectUrl = this.getSignUpUrl(params);
      window.location.assign(redirectUrl);
    });
  }

  signOut(): Promise<void> {
    logger.log('signOut()');
    const uri = this.getSignOutRedirectUri();

    const nosso = Boolean(this.flags.nosso);
    const params = nosso
      ? `?redirect_uri=${encodeURIComponent(uri)}&nosso=1`
      : `?redirect_uri=${encodeURIComponent(uri)}`;
    /* prettier-ignore */
    const promise: Promise<void | Response> = this.ownAceEndpoint ?
    fetch(`${this.ownAceEndpoint}/logout${params}`, {
      credentials: 'include'
    }).then(() => {
      logger.log('fetching logout');
    }) :
    Promise.resolve();

    return promise
      .then(() => {
        this.storage.clear();
        logger.log('cleared storage');
      })
      .then(() => {
        const url = this.getSignOutUrl();
        window.location.assign(url);
      });
  }

  setLocale(preferredLocale: ILocale): ILocale {
    logger.log('setLocale()', preferredLocale);
    this.locale = preferredLocale;

    return this.locale;
  }

  getOriginUrl(): Promise<IStorageData> {
    logger.log('getOriginUrl()');
    return this.storage.read(ORIGIN_KEY);
  }

  getSignInUrl(): string {
    logger.log('getSignInUrl()');
    const uri = this.signInRedirectUri || this.defaultRedirectUri || '';
    if (!uri) {
      CreateClient.throwCustomError('signInRedirectUri, defaultRedirectUri');
    }

    const queryParams = {
      client_id: this.clientId,
      redirect_uri: uri,
      locale: String(this.locale),
      ...(this.flags.nosso && { nosso: 1 })
    };

    return `${this.aceEndpoint}/signin?${stringify(queryParams)}`;
  }

  getSignUpUrl(params?: ISignUpOptions): string {
    logger.log('getSignUpUrl()', params);
    const referrer =
      params && params.referrer ? params.referrer : params && !params.referrer ? 'VIP' : '';

    const uri = this.signUpRedirectUri || this.defaultRedirectUri || '';

    if (!uri) {
      CreateClient.throwCustomError('signUpRedirectUri, defaultRedirectUri');
    }

    const queryParams = {
      referrer,
      client_id: this.clientId,
      redirect_uri: uri,
      locale: String(this.locale),
      ...(this.flags.nosso && { nosso: 1 })
    };

    return `${this.aceEndpoint}/signup?${stringify(queryParams)}`;
  }

  getSignOutUrl() {
    const uri = this.getSignOutRedirectUri();
    const queryParams = {
      client_id: this.clientId,
      redirect_uri: uri,
      locale: String(this.locale),
      ...(this.flags.nosso && { nosso: 1 })
    };

    return `${this.aceEndpoint}/signout?${stringify(queryParams)}`;
  }

  clearStorage(): Promise<void> {
    logger.log('clearStorage');

    return this.storage.clear();
  }

  getSignOutRedirectUri(): string {
    logger.log('getSignOutRedirectUri()');
    const uri = this.signOutRedirectUri || this.defaultRedirectUri || '';

    if (!uri) {
      CreateClient.throwCustomError('signOutRedirectUri, defaultRedirectUri');
    }

    return uri;
  }

  setSignOutRedirectUri(url: string): void {
    logger.log('setSignOutRedirectUri()', url);
    this.signOutRedirectUri = url;
  }

  private setOriginUrl(): Promise<void> {
    logger.log('setOriginUrl()');
    return this.storage.write(window.location.href, ORIGIN_KEY);
  }

  private redirectToSignIn(): void {
    logger.log('redirectToSignIn()');
    const url = this.getSignInUrl();

    window.location.assign(url);
  }

  private readTokensFromStorage(): Promise<IPrivateTokens> {
    logger.log('readTokensFromStorage()');
    return this.storage.read().then(tokens => {
      if (tokens) {
        logger.log('tokens read successfully');
        try {
          const parsedTokens = JSON.parse(tokens);
          logger.log('tokens parsed successfully');

          // Check expiration
          if (parsedTokens && parsedTokens.access_token) {
            return parsedTokens;
          }

          logger.log('tokens expired');
        } catch (error) {
          logger.log('error parse JSON tokens');

          this.storage.clear();

          logger.log('cleared storage');
        }
      } else {
        logger.log('no tokens in storage');
      }
    });
  }

  private async maybeWriteTokens(tokens: IPrivateTokens): Promise<void> {
    logger.log('maybeWriteTokens()', tokens);
    const currentTokens = await this.readTokensFromStorage();
    if (currentTokens) {
      const sameAccessToken = currentTokens.access_token === tokens.access_token;
      const sameIdToken = currentTokens.id_token === tokens.id_token;
      const sameRefreshToken = currentTokens.refresh_token === tokens.refresh_token;
      if (!(sameAccessToken && sameIdToken && sameRefreshToken)) {
        logger.log('Tokens are not equal. Saving new tokens');
        await this.storage.write(JSON.stringify(tokens));
        this.emit('tokens', tokens);
      }
    } else {
      logger.log('Save tokens to storage');
      await this.storage.write(JSON.stringify(tokens));
      this.emit('tokens', tokens);
    }
  }

  private fetchTokens() {
    logger.log('fetchTokens');

    this.initialTokensFetchDone = true;
    const nosso = Boolean(this.flags.nosso);
    const params = nosso ? '?nosso=1' : '';
    return fetch(`${this.ownAceEndpoint}/get-tokens${params}`, { credentials: 'include' }).then(
      response => {
        logger.log('tokens fetch response', response);

        if (response.status === 200) {
          return response
            .json()
            .then(parsedTokens => {
              logger.log('tokens parsed');
              return parsedTokens;
            })
            .catch(() => {
              logger.log('tokens invalid json');
              Promise.reject('Invalid json received');
            });
        }

        return Promise.reject(response);
      }
    );
  }

  private parseTokensFromUrl(): IPrivateTokens | null {
    logger.log('parseTokensFromUrl()');
    const query = parse(window.location.search.slice(1));

    if (query.access_token && query.id_token) {
      logger.log('tokens from url parsed');
      return {
        access_token: String(query.access_token),
        id_token: String(query.id_token),
        refresh_token: String(query.refresh_token)
      };
    }
    logger.log('Parse tokens from url failed. Looks like there is no access_token or id_token');
    return null;
  }

  private async scheduleRefreshTokens(tokens: ITokens): Promise<IPrivateTokens | void> {
    if (!this.ownAceEndpoint || !this.autorefresh) {
      return;
    }
    if (this.refreshTokenTimout) {
      clearTimeout(this.refreshTokenTimout);
    }
    const expires = Token.getExpirationDate(tokens.access_token).getTime();
    const now = Date.now();
    const refreshAt = expires - 120000;
    if (refreshAt > now) {
      this.refreshTokenTimout = setTimeout(async () => {
        await this.storage.clear();
        return this.refreshTokens().catch(() => {
          // do nothing
        });
        // tslint:disable-next-line:align
      }, refreshAt - now);
    } else {
      return this.refreshTokens();
    }
  }
}

function isUnauthorized(status: number): boolean {
  logger.log('isUnauthorized(). response status', status);
  return status === 401 || status === 403;
}

export { CreateClient, Token };
export * from './types';
